import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  FormHelperText,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { supabase } from '../../services/supabase';
import type { Game, GameResult, Player, RoleType, WinnerType } from '../../types/models';
import { apiCache } from '../../services/apiCache';
import { useAdminRole } from '../../contexts/AdminRoleContext';
import { fixTypes } from '../../services/adminPermissions';

// Cache keys and TTL
const GAME_RESULTS_CACHE_KEY = 'admin_game_results';
const GAMES_CACHE_KEY = 'admin_games_list';
const PLAYERS_CACHE_KEY = 'admin_players_list';
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes

const PLAYER_COUNT = 10; // Fixed number of players per game

interface GameResultsAdminProps {
  selectedGameId?: string | null;
  selectedLeagueId?: string | null;
  onSelectLeague?: (leagueId: string) => void;
  goToGames?: () => void;
}

const GameResultsAdmin: React.FC<GameResultsAdminProps> = ({ 
  selectedGameId: propSelectedGameId,
  selectedLeagueId,
  onSelectLeague,
  goToGames
}) => {
  const { userRole, isSuperAdmin, loading: roleLoading } = useAdminRole();
  const [gameResults, setGameResults] = useState<GameResult[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [localSelectedGameId, setLocalSelectedGameId] = useState<string | null>(null);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [currentResultIndex, setCurrentResultIndex] = useState<number | null>(null);
  
  // Form state
  const [editingResultId, setEditingResultId] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string>('');
  const [bonusPoints, setBonusPoints] = useState<number>(0);
  const [role, setRole] = useState<RoleType>('CITIZEN');
  const [isKilledFirst, setIsKilledFirst] = useState<boolean>(false);
  const [winningMove, setWinningMove] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  
  // Validation
  const [formErrors, setFormErrors] = useState({
    playerId: false
  });
  
  // Placeholder data for empty slots
  const emptyResults: (GameResult | null)[] = Array(PLAYER_COUNT).fill(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Fetch data when role information is available and when selectedGameId changes
  useEffect(() => {
    if (!roleLoading) {
      // Set the local selected game ID from props
      if (propSelectedGameId) {
        setLocalSelectedGameId(propSelectedGameId);
      }
      
      // Load all data in parallel
      Promise.all([
        fetchGames(),
        fetchPlayers()
      ]).then(() => {
        // Only fetch results if we have a selectedGameId
        if (propSelectedGameId) {
          fetchGameResults(propSelectedGameId);
        } else {
          // If no game is selected and goToGames function is available, redirect to Games
          if (goToGames) {
            goToGames();
          }
        }
      });
    }
  }, [roleLoading, propSelectedGameId, userRole?.id, goToGames]);

  // Update selectedGame when games or selectedGameId changes
  useEffect(() => {
    if (localSelectedGameId && games.length > 0) {
      const game = games.find(g => g.id === localSelectedGameId);
      setSelectedGame(game || null);
      
      // Update league selection if the game has a league and it's different from the current selection
      if (game && game.league_id && game.league_id !== selectedLeagueId && onSelectLeague) {
        onSelectLeague(game.league_id);
      }
    } else {
      setSelectedGame(null);
    }
  }, [games, localSelectedGameId, onSelectLeague, selectedLeagueId]);

  const getCacheKey = (baseKey: string, identifier: string = '') => {
    // Create user and data specific cache keys
    const userPart = userRole ? `_${userRole.id}_${userRole.role}` : '';
    const idPart = identifier ? `_${identifier}` : '';
    return `${baseKey}${userPart}${idPart}`;
  };

  const clearCaches = (gameId?: string) => {
    // Clear all related caches when data changes
    apiCache.remove(getCacheKey(GAMES_CACHE_KEY));
    
    // If gameId is provided, only clear that specific game's results cache
    if (gameId) {
      apiCache.remove(getCacheKey(GAME_RESULTS_CACHE_KEY, gameId));
    } else {
      // Otherwise clear all game results cache (this is a sledgehammer approach)
      // In a larger app, you might want to be more selective
      apiCache.clear();
    }
  };

  const fetchGameResults = async (gameId: string) => {
    setLoading(true);
    try {
      // Get from cache or fetch
      const resultsData = await apiCache.getOrFetch<GameResult[]>(
        getCacheKey(GAME_RESULTS_CACHE_KEY, gameId),
        async () => {
          
          const { data, error } = await supabase
            .from('game_results')
            .select(`
              *,
              player:player_id (
                id,
                nickname,
                full_name
              )
            `)
            .eq('game_id', gameId)
            .order('player_num', { ascending: true });
          
          if (error) {
            throw error;
          }
          
          return fixTypes<GameResult>(data || []);
        },
        CACHE_TTL
      );
      
      setGameResults(resultsData);
    } catch (error: any) {
      console.error('Error fetching game results:', error.message);
      showSnackbar('Failed to load game results', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchGames = async () => {
    try {
      // Get from cache or fetch
      const gamesData = await apiCache.getOrFetch<Game[]>(
        getCacheKey(GAMES_CACHE_KEY),
        async () => {
          
          let queryResult;
          
          if (isSuperAdmin) {
            // Superadmins see all games
            queryResult = await supabase
              .from('games')
              .select(`
                *,
                league:league_id (
                  id,
                  name
                ),
                judge:judge_id (
                  id,
                  nickname,
                  full_name
                )
              `)
              .order('session_num', { ascending: false })
              .order('number_in_session', { ascending: false });
          } else if (userRole?.club_id) {
            // Regular admins only see games from their club's leagues
            // First get leagues from the admin's club
            const { data: leagues } = await supabase
              .from('leagues')
              .select('id')
              .eq('club_id', userRole.club_id);
              
            const leagueIds = leagues?.map(league => league.id) || [];
            
            if (leagueIds.length === 0) {
              // No leagues in this club, return empty result
              return [];
            } else {
              // Get games for these leagues
              queryResult = await supabase
                .from('games')
                .select(`
                  *,
                  league:league_id (
                    id,
                    name
                  ),
                  judge:judge_id (
                    id,
                    nickname,
                    full_name
                  )
                `)
                .in('league_id', leagueIds)
                .order('session_num', { ascending: false })
                .order('number_in_session', { ascending: false });
            }
          } else {
            // No club assigned
            return [];
          }
          
          const { data, error } = queryResult;
          
          if (error) {
            throw error;
          }
          
          return fixTypes<Game>(data || []);
        },
        CACHE_TTL
      );
      
      setGames(gamesData);
    } catch (error: any) {
      console.error('Error fetching games:', error.message);
      showSnackbar('Failed to load games', 'error');
    }
  };

  const fetchPlayers = async () => {
    try {
      // Get from cache or fetch from API
      const playersData = await apiCache.getOrFetch<Player[]>(
        getCacheKey(PLAYERS_CACHE_KEY),
        async () => {
          
          // Players don't have club_id, so no need for admin filtering
          const { data, error } = await supabase
            .from('players')
            .select('*')
            .order('nickname', { ascending: true });
          
          if (error) {
            throw error;
          }
          
          return fixTypes<Player>(data || []);
        },
        CACHE_TTL
      );
      
      setPlayers(playersData);
    } catch (error: any) {
      console.error('Error fetching players:', error.message);
      showSnackbar('Failed to load players', 'error');
    }
  };

  const handleOpenDialog = (resultIndex: number, existingResult?: GameResult) => {
    if (existingResult) {
      setEditingResultId(existingResult.id);
      setPlayerId(existingResult.player_id);
      setBonusPoints(existingResult.bonus_points);
      setRole(existingResult.role as RoleType);
      setIsKilledFirst(existingResult.is_killed_first);
      setWinningMove(existingResult.winning_move || '');
      setComment(existingResult.comment || '');
    } else {
      resetForm();
    }
    setCurrentResultIndex(resultIndex);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const resetForm = () => {
    setEditingResultId(null);
    setPlayerId('');
    setBonusPoints(0);
    setRole('CITIZEN');
    setIsKilledFirst(false);
    setWinningMove('');
    setComment('');
    setFormErrors({
      playerId: false
    });
  };

  const validateForm = (): boolean => {
    const errors = {
      playerId: !playerId
    };
    
    setFormErrors(errors);
    
    return !Object.values(errors).some(Boolean);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handlePlayerChange = (event: SelectChangeEvent) => {
    setPlayerId(event.target.value);
  };

  const handleRoleChange = (event: SelectChangeEvent) => {
    setRole(event.target.value as RoleType);
  };

  const handleSubmit = async () => {
    if (!validateForm() || !localSelectedGameId || currentResultIndex === null) {
      return;
    }

    try {
      const resultData = {
        game_id: localSelectedGameId,
        player_id: playerId,
        bonus_points: bonusPoints,
        role,
        is_killed_first: isKilledFirst,
        winning_move: winningMove.trim() || null,
        comment: comment.trim() || null,
        player_num: currentResultIndex + 1, // 1-based index for player positions
        is_win: selectedGame?.winner === 'TIE' 
          ? false 
          : selectedGame?.winner === 'CIVILIANS' 
            ? (role === 'CITIZEN' || role === 'SHERIFF') 
            : (role === 'MAFIA' || role === 'DON')
      };

      if (editingResultId) {
        // Update existing result
        const { error } = await supabase
          .from('game_results')
          .update(resultData)
          .eq('id', editingResultId);

        if (error) throw error;
        showSnackbar('Game result updated successfully', 'success');
      } else {
        // Create new result
        const { error } = await supabase
          .from('game_results')
          .insert([resultData]);

        if (error) throw error;
        showSnackbar('Game result created successfully', 'success');
      }

      // Clear cache for this specific game
      clearCaches(localSelectedGameId);
      
      handleCloseDialog();
      if (localSelectedGameId) {
        fetchGameResults(localSelectedGameId);
      }
    } catch (error: any) {
      console.error('Error saving game result:', error.message);
      showSnackbar(`Failed to ${editingResultId ? 'update' : 'create'} game result`, 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this game result?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('game_results')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Clear cache for this specific game
      if (localSelectedGameId) {
        clearCaches(localSelectedGameId);
      }
      
      showSnackbar('Game result deleted successfully', 'success');
      if (localSelectedGameId) {
        fetchGameResults(localSelectedGameId);
      }
    } catch (error: any) {
      console.error('Error deleting game result:', error.message);
      showSnackbar('Failed to delete game result', 'error');
    }
  };

  const getRoleChipColor = (role: RoleType) => {
    switch (role) {
      case 'CITIZEN':
        return 'primary';
      case 'SHERIFF':
        return 'success';
      case 'MAFIA':
        return 'error';
      case 'DON':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPlayerName = (result: GameResult) => {
    if (result.player) {
      const player = result.player as any;
      return player.nickname || 'Unknown Player';
    }
    
    if (result.player_id) {
      const player = players.find(p => p.id === result.player_id);
      return player?.nickname || 'Unknown Player';
    }
    
    return 'Unknown Player';
  };

  // Create array of results with empty slots filled in
  const getFormattedResults = () => {
    const resultsCopy = [...emptyResults];
    gameResults.forEach(result => {
      const playerNum = result.player_num;
      if (playerNum >= 1 && playerNum <= PLAYER_COUNT) {
        resultsCopy[playerNum - 1] = result;
      }
    });
    return resultsCopy;
  };

  // Show loading if we're waiting for role data or initial data fetch
  if (roleLoading || (loading && !gameResults.length)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {goToGames && (
            <IconButton 
              color="primary" 
              sx={{ mr: 1 }}
              onClick={goToGames}
              aria-label="back to games"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" component="h2">
            {selectedGame ? (
              <>
                Game Results: Session {selectedGame.session_num}, Game {selectedGame.number_in_session}
                {selectedGame.league && (
                  <Typography component="span" variant="subtitle1" sx={{ ml: 1, color: 'text.secondary' }}>
                    ({(selectedGame.league as any).name})
                  </Typography>
                )}
              </>
            ) : (
              'Game Results'
            )}
          </Typography>
        </Box>
        
        {!selectedGame && (
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="game-select-label">Select Game</InputLabel>
            <Select
              labelId="game-select-label"
              value={localSelectedGameId || ''}
              label="Select Game"
              onChange={(e) => setLocalSelectedGameId(e.target.value || null)}
            >
              {games.map((game) => (
                <MenuItem key={game.id} value={game.id}>
                  Session {game.session_num}, Game {game.number_in_session}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {!selectedGame ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography>{games.length > 0 ? 'Please select a game to view its results' : 'No games found'}</Typography>
        </Paper>
      ) : (
        <>
          {/* Game info card */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">JUDGE</Typography>
                <Typography variant="body1">{(selectedGame.judge as any)?.nickname || 'Unknown'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">WINNER</Typography>
                <Chip 
                  label={selectedGame.winner} 
                  color={selectedGame.winner === 'CIVILIANS' ? 'primary' : (selectedGame.winner === 'MAFIA' ? 'error' : 'default')}
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">DATE</Typography>
                <Typography variant="body1">
                  {new Date(selectedGame.occurred_at).toLocaleDateString()}
                </Typography>
              </Box>
              {selectedGame.video_link && (
                <Box sx={{ ml: 'auto' }}>
                  <Button 
                    variant="outlined" 
                    href={selectedGame.video_link} 
                    target="_blank"
                    startIcon={<EmojiEventsIcon />}
                  >
                    Watch Video
                  </Button>
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Game results table */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.paper' }}>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Bonus</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Special</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFormattedResults().map((result, index) => (
                  <TableRow 
                    key={result?.id || `empty-${index}`} 
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      bgcolor: result ? (
                        result.is_win ? 'rgba(46, 125, 50, 0.08)' : 'rgba(211, 47, 47, 0.08)'
                      ) : 'inherit'
                    }}
                  >
                    <TableCell align="center" component="th" scope="row">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      {result ? (
                        getPlayerName(result)
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No player assigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {result ? (
                        <Chip 
                          label={result.role} 
                          color={getRoleChipColor(result.role as RoleType)}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {result ? (
                        <Chip 
                          label={result.bonus_points > 0 ? `+${result.bonus_points}` : `${result.bonus_points}`} 
                          color={result.bonus_points > 0 ? 'success' : (result.bonus_points < 0 ? 'error' : 'default')}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {result && (
                        <Stack direction="row" spacing={1}>
                          {result.is_killed_first && (
                            <Tooltip title="Killed First">
                              <Chip 
                                icon={<PersonOffIcon sx={{ fontSize: '0.8rem' }} />} 
                                label="FIRST KILL" 
                                color="error"
                                size="small"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                          {result.winning_move && (
                            <Tooltip title="Winning Move">
                              <Chip 
                                icon={<SportsKabaddiIcon sx={{ fontSize: '0.8rem' }} />} 
                                label="WINNING MOVE" 
                                color="success"
                                size="small"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {result ? (
                          <>
                            <IconButton onClick={() => handleOpenDialog(index, result)} color="primary" size="small">
                              <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDelete(result.id)} color="error" size="small">
                              <DeleteIcon />
                            </IconButton>
                          </>
                        ) : (
                          <Button 
                            variant="outlined" 
                            color="primary" 
                            size="small"
                            onClick={() => handleOpenDialog(index)}
                            startIcon={<AddIcon />}
                          >
                            Add
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Game result info section */}
          {gameResults.some(r => r.comment) && (
            <Paper sx={{ mt: 3, p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Notes</Typography>
              {gameResults.filter(r => r.comment).map(result => (
                <Box key={result.id} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {getPlayerName(result)} ({result.role})
                  </Typography>
                  <Typography variant="body2">{result.comment}</Typography>
                </Box>
              ))}
            </Paper>
          )}

          {/* Dialog for adding/editing result */}
          <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              {editingResultId ? 'Edit Player Results' : 'Add Player Results'} 
              {currentResultIndex !== null && (
                <Typography component="span" color="text.secondary">
                  {` - Position ${currentResultIndex + 1}`}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <FormControl fullWidth variant="outlined" error={formErrors.playerId}>
                  <InputLabel id="player-select-label">Player</InputLabel>
                  <Select
                    labelId="player-select-label"
                    id="player-select"
                    value={playerId}
                    onChange={handlePlayerChange}
                    label="Player"
                  >
                    {players.map((player) => (
                      <MenuItem key={player.id} value={player.id}>
                        {player.nickname} ({player.full_name})
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.playerId && <FormHelperText>Please select a player</FormHelperText>}
                </FormControl>
                
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="role-select-label">Role</InputLabel>
                  <Select
                    labelId="role-select-label"
                    id="role-select"
                    value={role}
                    onChange={handleRoleChange}
                    label="Role"
                  >
                    <MenuItem value="CITIZEN">Citizen</MenuItem>
                    <MenuItem value="SHERIFF">Sheriff</MenuItem>
                    <MenuItem value="MAFIA">Mafia</MenuItem>
                    <MenuItem value="DON">Don</MenuItem>
                  </Select>
                  <FormHelperText>
                    Win status is determined automatically based on the game's winner and the player's role
                  </FormHelperText>
                </FormControl>
                
                <TextField
                  margin="dense"
                  id="bonusPoints"
                  label="Bonus Points"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={bonusPoints}
                  onChange={(e) => setBonusPoints(Number(e.target.value))}
                  InputProps={{ 
                    startAdornment: (
                      <InputAdornment position="start">
                        {bonusPoints >= 0 ? '+' : ''}
                      </InputAdornment>
                    )
                  }}
                  helperText="Additional points awarded (positive or negative)"
                />
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={isKilledFirst} 
                      onChange={(e) => setIsKilledFirst(e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ mr: 1 }}>Killed First</Typography>
                      <Tooltip title="First player eliminated from the game">
                        <WarningIcon fontSize="small" color="warning" />
                      </Tooltip>
                    </Box>
                  }
                />
                
                <TextField
                  margin="dense"
                  id="winningMove"
                  label="Winning Move (Optional)"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={winningMove}
                  onChange={(e) => setWinningMove(e.target.value)}
                  placeholder="E.g., Final accusation that led to victory"
                  helperText="Brief description of a critical play that decisively contributed to the win"
                />
                
                <TextField
                  margin="dense"
                  id="comment"
                  label="Comments (Optional)"
                  type="text"
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Notable events or explanations..."
                  helperText="Any additional notes about the player's performance"
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseDialog} color="primary" sx={{ borderRadius: 2 }}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained" 
                color="secondary"
                sx={{ borderRadius: 2 }}
              >
                {editingResultId ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GameResultsAdmin;